import React, { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { supabase } from '../supabaseClient';

const UserPreferences = () => {
    const { user } = useAuth();
    const [niche, setNiche] = useState('');
    const [contentGoal, setContentGoal] = useState('');
    const [preferredPlatforms, setPreferredPlatforms] = useState([]);
    const [toneStyle, setToneStyle] = useState('');
    const [targetAudience, setTargetAudience] = useState('');
    const [videoLengthPreference, setVideoLengthPreference] = useState('');
    const [languageRegion, setLanguageRegion] = useState('');
    const [message, setMessage] = useState('');
    const [viralContent, setViralContent] = useState([]);
    const [preferencesId, setPreferencesId] = useState(null); // New state variable to hold the preferences ID

    useEffect(() => {
        const fetchUserPreferences = async () => {
            if (user) {
                try {
                    const { data: profileData, error: profileError } = await supabase
                        .from('profiles')
                        .select('id')
                        .eq('user_id', user.id)
                        .single();

                    if (profileError) {
                        console.error('Error fetching profile:', profileError);
                        setMessage('Error fetching profile data.');
                        return;
                    }
                    if (profileData) {
                        const { data: preferencesData, error: preferencesError } = await supabase
                            .from('user_preferences')
                            .select('*')
                            .eq('profile_id', profileData.id)
                            .single();

                        if (preferencesError) {
                            console.error('Error fetching user preferences:', preferencesError);
                            setMessage('Error fetching user preferences.');
                        } else if (preferencesData) {
                            setPreferencesId(preferencesData.id);
                            setNiche(preferencesData.niche || '');
                            setContentGoal(preferencesData.content_goal || '');
                            setPreferredPlatforms(preferencesData.preferred_platforms || []);
                            setToneStyle(preferencesData.tone_style || '');
                            setTargetAudience(preferencesData.target_audience || '');
                            setVideoLengthPreference(preferencesData.video_length_preference || '');
                            setLanguageRegion(preferencesData.language_region || '');
                        }
                    }
                } catch (error) {
                    console.error('An unexpected error occurred:', error);
                    setMessage('An unexpected error occurred.');
                }
            }
        };

        fetchUserPreferences();
    }, [user]);

    const handleSubmit = async (e) => {
        e.preventDefault();
        if (!user) {
            setMessage('You must be logged in to save preferences.');
            return;
        }

        try {
            const { data: profileData, error: profileError } = await supabase
                .from('profiles')
                .select('id')
                .eq('user_id', user.id)
                .single();

            if (profileError) {
                console.error('Error fetching profile:', profileError);
                setMessage('Failed to fetch profile data.');
                return;
            }

            // Preferences data
            const preferences = {
                profile_id: profileData.id,
                niche,
                content_goal: contentGoal,
                preferred_platforms: preferredPlatforms,
                tone_style: toneStyle,
                target_audience: targetAudience,
                video_length_preference: videoLengthPreference,
                language_region: languageRegion,
            };

            if (preferencesId) {
                // Update existing record
                const { error: updateError } = await supabase
                    .from('user_preferences')
                    .update(preferences)
                    .eq('id', preferencesId);

                if (updateError) {
                    console.error('Error updating user preferences:', updateError);
                    setMessage('Failed to update preferences.');
                } else {
                    setMessage('Preferences updated successfully!');
                }
            } else {
                // Insert new record
                const { data, error: insertError } = await supabase
                    .from('user_preferences')
                    .insert([preferences])
                    .select(); // Fetch the newly inserted row

                if (insertError) {
                    console.error('Error inserting user preferences:', insertError);
                    setMessage('Failed to save preferences.');
                } else {
                    setMessage('Preferences saved successfully!');
                     // If insert is successful, update preferencesId with the id of the newly created record
                    if (data && data.length > 0) {
                    setPreferencesId(data[0].id);
                    }
                }
            }
        } catch (error) {
            console.error('An unexpected error occurred:', error);
            setMessage('An unexpected error occurred.');
        }
    };

    const handleAnalyzeTrends = async () => {
        if (!niche) {
            setMessage('Please enter your niche to analyze trends.');
            return;
        }

        try {
            const { data, error } = await supabase
                .from('viral_trends')
                .select('*')
                .ilike('niche', `%${niche}%`)
                .limit(4);

            if (error) {
                console.error('Error fetching viral trends:', error);
                setMessage('Failed to fetch viral trends.');
            } else {
                setViralContent(data || []);
                setMessage('');
            }
        } catch (error) {
            console.error('An unexpected error occurred:', error);
            setMessage('An unexpected error occurred.');
        }
    };

    return (
        <div className="flex-1 bg-gray-900 p-6 overflow-y-auto">
            <h2 className="text-2xl font-bold text-white mb-8">Your Profile</h2>

            <form onSubmit={handleSubmit} className="space-y-6">
                <div className="space-y-6">
                    <div className="bg-gray-800/50 rounded-xl p-6 border border-gray-700/50">
                        <h3 className="text-xl font-bold mb-4 text-white">Content Preferences</h3>
                        <div className="space-y-4">
                            <div>
                                <label className="block text-sm font-medium text-gray-400 mb-1">Niche</label>
                                <input
                                    type="text"
                                    placeholder="e.g., Fitness, Technology, Fashion"
                                    value={niche}
                                    onChange={(e) => setNiche(e.target.value)}
                                    className="w-full p-3 bg-gray-900 border border-gray-700 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 text-white placeholder-gray-500"
                                />
                            </div>
                            <div>
                                <label className="block text-sm font-medium text-gray-400 mb-1">Content Goal</label>
                                <input
                                    type="text"
                                    placeholder="e.g., Grow audience, Monetize content"
                                    value={contentGoal}
                                    onChange={(e) => setContentGoal(e.target.value)}
                                    className="w-full p-3 bg-gray-900 border border-gray-700 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 text-white placeholder-gray-500"
                                />
                            </div>
                            <div>
                                <label className="block text-sm font-medium text-gray-400 mb-1">Target Audience</label>
                                <input
                                    type="text"
                                    placeholder="e.g., Young adults, Professionals"
                                    value={targetAudience}
                                    onChange={(e) => setTargetAudience(e.target.value)}
                                    className="w-full p-3 bg-gray-900 border border-gray-700 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 text-white placeholder-gray-500"
                                />
                            </div>
                        </div>
                    </div>

                    <div className="bg-gray-800/50 rounded-xl p-6 border border-gray-700/50">
                        <h3 className="text-xl font-bold mb-4 text-white">Platform & Style</h3>
                        <div className="space-y-4">
                            <div>
                                <label className="block text-sm font-medium text-gray-400 mb-1">Preferred Platforms</label>
                                <input
                                    type="text"
                                    placeholder="e.g., Instagram, TikTok, YouTube"
                                    value={preferredPlatforms.join(', ')}
                                    onChange={(e) => setPreferredPlatforms(e.target.value.split(',').map(p => p.trim()))}
                                    className="w-full p-3 bg-gray-900 border border-gray-700 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 text-white placeholder-gray-500"
                                />
                            </div>
                            <div>
                                <label className="block text-sm font-medium text-gray-400 mb-1">Content Tone</label>
                                <input
                                    type="text"
                                    placeholder="e.g., Professional, Casual, Humorous"
                                    value={toneStyle}
                                    onChange={(e) => setToneStyle(e.target.value)}
                                    className="w-full p-3 bg-gray-900 border border-gray-700 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 text-white placeholder-gray-500"
                                />
                            </div>
                        </div>
                    </div>

                    <div className="bg-gray-800/50 rounded-xl p-6 border border-gray-700/50">
                        <h3 className="text-xl font-bold mb-4 text-white">Additional Settings</h3>
                        <div className="space-y-4">
                            <div>
                                <label className="block text-sm font-medium text-gray-400 mb-1">Video Length</label>
                                <input
                                    type="text"
                                    placeholder="e.g., Short-form, Long-form"
                                    value={videoLengthPreference}
                                    onChange={(e) => setVideoLengthPreference(e.target.value)}
                                    className="w-full p-3 bg-gray-900 border border-gray-700 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 text-white placeholder-gray-500"
                                />
                            </div>
                            <div>
                                <label className="block text-sm font-medium text-gray-400 mb-1">Language/Region</label>
                                <input
                                    type="text"
                                    placeholder="e.g., English/US, Spanish/Mexico"
                                    value={languageRegion}
                                    onChange={(e) => setLanguageRegion(e.target.value)}
                                    className="w-full p-3 bg-gray-900 border border-gray-700 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 text-white placeholder-gray-500"
                                />
                            </div>
                        </div>
                    </div>
                </div>

                <div className="pt-4 space-y-3">
                    <button
                        type="submit"
                        className="w-full p-3 bg-blue-500 text-white rounded-full hover:bg-blue-600 transition-all duration-200 font-bold focus:outline-none focus:ring-2 focus:ring-white"
                    >
                        Save Preferences
                    </button>
                    <button
                        type="button"
                        onClick={handleAnalyzeTrends}
                        className="w-full p-3 bg-gray-700 text-white rounded-full hover:bg-gray-600 transition-all duration-200 font-bold focus:outline-none focus:ring-2 focus:ring-white"
                    >
                        Analyze Viral Trends
                    </button>
                </div>
            </form>

            {message && (
                <div className="mt-6 text-center">
                    <p className={`py-2 px-4 rounded-full inline-block ${message.includes('success') ? 'bg-blue-500 bg-opacity-20 text-blue-400' : 'bg-red-500 bg-opacity-20 text-red-400'}`}>
                        {message}
                    </p>
                </div>
            )}

            {viralContent.length > 0 && (
                <div className="mt-8 bg-gray-800/50 rounded-xl p-6 border border-gray-700/50">
                    <h3 className="text-xl font-bold mb-4 flex items-center text-white">
                        <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 mr-2 text-blue-400" viewBox="0 0 20 20" fill="currentColor">
                            <path fillRule="evenodd" d="M12.395 2.553a1 1 0 00-1.45-.385c-.345.23-.614.558-.822.88-.214.33-.403.713-.57 1.116-.334.804-.614 1.768-.84 2.734a31.365 31.365 0 00-.613 3.58 2.64 2.64 0 01-.945-1.067c-.328-.68-.398-1.534-.398-2.654A1 1 0 005.05 6.05 6.981 6.981 0 003 11a7 7 0 1011.95-4.95c-.592-.591-.98-.985-1.348-1.467-.363-.476-.724-1.063-1.207-2.03zM12.12 15.12A3 3 0 017 13s.879.5 2.5.5c0-1 .5-4 1.25-4.5.5 1 .786 1.293 1.371 1.879A2.99 2.99 0 0113 13a2.99 2.99 0 01-.879 2.121z" clipRule="evenodd" />
                        </svg>
                        Trending Content
                    </h3>
                    <div className="grid gap-4 md:grid-cols-2">
                        {viralContent.map((item, idx) => (
                            <div key={idx} className="bg-gray-900 p-4 rounded-lg hover:bg-gray-800 transition-all duration-200">
                                <p className="font-bold text-white">{item.creator_name}</p>
                                <p className="text-sm text-gray-400 mt-1 mb-3">{item.description}</p>
                                <a
                                    href={item.content_url}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    className="inline-flex items-center text-blue-400 hover:underline text-sm font-medium"
                                >
                                    <span>View Content</span>
                                    <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4 ml-1" viewBox="0 0 20 20" fill="currentColor">
                                        <path fillRule="evenodd" d="M10.293 5.293a1 1 0 011.414 0l4 4a1 1 0 010 1.414l-4 4a1 1 0 01-1.414-1.414L12.586 11H5a1 1 0 110-2h7.586l-2.293-2.293a1 1 0 010-1.414z" clipRule="evenodd" />
                                    </svg>
                                </a>
                            </div>
                        ))}
                    </div>
                </div>
            )}
        </div>
    );
};

export default UserPreferences;
